import { Box, Typography } from "@mui/material"
import type { FC, ReactNode } from "react"

import type { Location } from "@/graphql/codegen/graphql"
import type { ExistingSpecialRow } from "@/screens/Sales/hooks/useGetCustomerPricing"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

interface SpecialPricingHeaderProps {
  row: ExistingSpecialRow
}

const MISSING_VALUE = "-"

const formatDateLabel = (date?: string | null): string => {
  if (!date) return MISSING_VALUE
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return MISSING_VALUE
  return parsed.toLocaleDateString("en-US")
}

const formatLocationOrDash = (location?: Location | null, format?: AddressFormat): string => {
  const formatted = formatLocationAddress(location, format)
  return formatted || MISSING_VALUE
}

const Pill: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box className='rounded bg-tertiary-orange-100 px-2'>
    <Typography component='span' className='text-xs font-semibold text-gray-800'>
      {label}
    </Typography>{" "}
    <Typography component='span' className='text-xs text-gray-700'>
      {value}
    </Typography>
  </Box>
)

const InlineLabel: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <Typography className='text-sm text-gray-800'>
    <Typography component='span' className='text-sm font-medium text-gray-800'>
      {label}:
    </Typography>{" "}
    {value}
  </Typography>
)

export const SpecialPricingHeader: FC<SpecialPricingHeaderProps> = ({ row }) => {
  const { item, shipFromWarehouseArea, incoterms, incotermsLocation } = row
  const effective = formatDateLabel(item.effectiveDate)
  const validUntil = formatDateLabel(item.expirationDate)
  const shipFrom = formatLocationOrDash(
    shipFromWarehouseArea?.location as Location | null | undefined,
    AddressFormat.LocationAndAddress
  )
  const incotermsLabel = incoterms || MISSING_VALUE
  const incotermsLocationLabel = formatLocationOrDash(
    incotermsLocation as Location | null | undefined,
    AddressFormat.LocationAndAddress
  )

  return (
    <Box className='flex flex-col gap-1 bg-tertiary-orange-300 px-3 py-2'>
      <Box className='flex flex-wrap items-center gap-3'>
        <Typography className='text-base font-medium text-gray-800'>Special Pricing</Typography>
        <Box className='flex flex-wrap items-center gap-2'>
          <Pill label='Effective' value={effective} />
          <Pill label='Valid Until' value={validUntil} />
        </Box>
      </Box>
      <InlineLabel label='Ship From' value={shipFrom} />
      <Box className='flex flex-wrap items-center gap-x-6 gap-y-1'>
        <InlineLabel label='Incoterms' value={incotermsLabel} />
        <InlineLabel label='Incoterms Location' value={incotermsLocationLabel} />
      </Box>
    </Box>
  )
}
